import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Popover, IconButton } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

interface VideoThumbnailProps {
    file: File;
    videoThumbnailURL? : string
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ file, videoThumbnailURL }) => {
    const [thumbnail, setThumbnail] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [videoUrl, setVideoUrl] = useState<string>('');
    const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

    useEffect(() => {
        // Generate a thumbnail from the video
        const generateThumbnail = () => {
            const videoUrl = URL.createObjectURL(file);
            setThumbnailUrl(videoUrl); // Store the video URL to revoke later

            const video = document.createElement('video');
            video.src = videoUrl;
            video.crossOrigin = 'anonymous';
            video.muted = true;

            video.addEventListener('loadeddata', () => {
                video.currentTime = 1;
            });

            video.addEventListener('seeked', () => {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const imageUrl = canvas.toDataURL('image/png');
                    setThumbnail(imageUrl);
                }
                // Revoke the video URL after we're done with it
                URL.revokeObjectURL(videoUrl);
            });
        };

        generateThumbnail();

        // Cleanup function to revoke object URLs
        return () => {
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [file]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        // Create the video URL for the popover
        const videoUrl = URL.createObjectURL(file);
        setVideoUrl(videoUrl);
    };

    const handleClose = () => {
        setAnchorEl(null);
        // Revoke the video URL
        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            setVideoUrl('');
        }
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Tooltip title={file.name} arrow>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        cursor: 'pointer',
                    }}
                    onClick={handleClick}
                >
                    {thumbnail ? (
                        <img
                            src={thumbnail}
                            alt="Video Thumbnail"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 8,
                            }}
                        />
                    ) : videoThumbnailURL ? 
                            <img
                                src={videoThumbnailURL}
                                alt="Video Thumbnail"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                }}
                            />
                    :
                        (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'black',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 8,
                            }}
                        >
                            <PlayCircleOutlineIcon
                                sx={{ fontSize: 40, color: 'white', opacity: 0.7 }}
                            />
                        </Box>
                    )}
                    <PlayCircleOutlineIcon
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: 40,
                            color: 'white',
                            opacity: 0.7,
                        }}
                    />
                </Box>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                PaperProps={{
                    style: { borderRadius: 8, overflow: 'hidden' },
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: { xs: '300px', sm: '400px' },
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                    }}
                >
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.7)',
                            },
                        }}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                    <video
                        src={videoUrl}
                        controls
                        style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: 8,
                        }}
                    />
                </Box>
            </Popover>
        </>
    );
};

export default VideoThumbnail;
